import axios from "axios";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import {
  ExperienceActionRequestBodyType,
  ExperienceActionRequestPathParamsType,
  ExperienceActionResponseBodyType,
  TabEnumFe,
  TabType,
} from "@intelligems/ig-types";
import { initExperimentFromDto } from "@src/store/slices/experiences";
import { setIgnoreUpdateDetected } from "@src/store/slices/global";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mpPauseExperience } from "../../utils/mixpanel";
import {
  experienceKeys,
  historyKeys,
  offerKeys,
  scheduleKeys,
} from "../queryKeys";
import { ApiRequest } from "../types/api";

import { experienceAction } from "./experience-action";

export const usePauseExperience = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  return useMutation<
    ExperienceActionResponseBodyType,
    Error,
    ApiRequest<
      ExperienceActionRequestBodyType,
      ExperienceActionRequestPathParamsType & {
        name: string;
      }
    >
  >({
    mutationKey: ["pauseExperience"],
    onMutate: () => {
      dispatch(setIgnoreUpdateDetected(true));
    },
    mutationFn: async (data) => experienceAction(data),
    onSuccess: async (data, payload) => {
      void queryClient.invalidateQueries({
        queryKey:
          data.experience?.category === "experiment"
            ? experienceKeys.experimentList
            : experienceKeys.personalizationList,
      });
      void queryClient.invalidateQueries({
        queryKey: historyKeys.one(payload.pathParams.experienceId),
      });
      void queryClient.invalidateQueries({
        queryKey: offerKeys.all,
      });

      mpPauseExperience({
        name: payload.pathParams.name,
        id: payload.pathParams.experienceId,
      });

      if (data.experience?.id) {
        await queryClient.invalidateQueries({
          queryKey: experienceKeys.one(data.experience.id),
        });
        await queryClient.invalidateQueries({
          queryKey: scheduleKeys.one(data.experience?.id),
        });

        dispatch(
          initExperimentFromDto({
            experience: data.experience,
            testFlowType: data.experience.type,
            tab: (searchParams.get("tab") as TabType) ?? TabEnumFe.ANALYTICS,
          })
        );
      }
    },
    onError: (error) => {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 503) {
          toast.info("Pausing experience...");
        } else {
          toast.error("Error pausing experience");
        }
      }
    },
  });
};
